import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import { MoreHorizontal } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { addTab, setActiveTab, setListViewState, setStatusFilter } from "../../../../redux/slices/ordersSlice";
import OrderUtil from "../utils/OrderUtil";
import CancelOrderModal from "../Modal/CancelOrderModal";
import axios from "axios";
import Cookies from "js-cookie";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import { selectModuleTabs } from "../../../../redux/selectors/tabsSelectors";
import { addTab as addShipmentTab } from "../../../../redux/slices/shipmentSlice";
import { useNavigate } from "react-router-dom";
import {
  useTableSavedViews,
  TableSavedViewsControl,
  TABLE_PAGE_KEYS,
} from "../../../TableSavedViews";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const statusBadgeClass = {
  NEW:              "bg-blue-100 dark:bg-blue-900/40 text-black dark:text-blue-200",
  ASSIGNED:         "bg-amber-100 dark:bg-amber-900/40 text-black dark:text-amber-200",
  UNASSIGNED:       "bg-neutral-200 dark:bg-neutral-700/40 text-black dark:text-gray-200",
  ACCEPTED:         "bg-emerald-100 dark:bg-emerald-900/40 text-black dark:text-emerald-200",
  TENDERED:         "bg-indigo-100 dark:bg-indigo-900/40 text-black dark:text-indigo-200",
  PLANNED:          "bg-sky-300 dark:bg-sky-900/50 text-black dark:text-sky-300",
  "READY FOR PICKUP":"bg-cyan-100 dark:bg-cyan-900/40 text-black dark:text-cyan-200",
  ACTIVE:           "bg-teal-100 dark:bg-teal-900/40 text-black dark:text-teal-200",
  COMPLETED:        "bg-green-100 dark:bg-green-900/40 text-black dark:text-green-200",
  INVOICED:         "bg-purple-100 dark:bg-purple-900/40 text-black dark:text-purple-200",
  CANCELLED:        "bg-red-100 dark:bg-red-900/40 text-black dark:text-red-200",
  DEFAULT:          "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200",
};

const makeKey       = (orderNumber, officeCode) => `${orderNumber}::${officeCode}`;
const toErrorMessage = (err) => {
  const data = err?.response?.data;
  if (!data) return err?.message || "Unknown error";
  if (typeof data === "string") return data;
  return data.content || data.message || JSON.stringify(data);
};

// Column IDs that map to Table headers — the order here is the default render order.
const TABLE_HEADERS = [
  "Action",           // index 0 — checkbox (utility, always pinned left)
  "Selection",        // index 1 — row-action (utility, always pinned left)
  "Order Number",     // index 2 — first data column (pinned left by default)
  "Office Code",
  "Status",
  "Shipment Number",
  "Origin Name",
  "Payment Method",
  "Destination Name",
  "Destination City",
  "Destination State",
  "PO Number",
  "Comments",
  "Pickup Date",
  "Deliver Date",
  "Weight",
  "Pallets",
  "Requested Mode",
  "Created Date",
];

// Per-column size overrides
const COLUMN_SIZE_CONFIG = {
  "Order Number":     { size: 160, minSize: 120, maxSize: 300 },
  "Office Code":      { size: 120, minSize: 80,  maxSize: 180 },
  "Status":           { size: 140, minSize: 100, maxSize: 200 },
  "Shipment Number":  { size: 160, minSize: 120, maxSize: 260 },
  "Origin Name":      { size: 200, minSize: 140, maxSize: 360 },
  "Payment Method":   { size: 140, minSize: 100, maxSize: 220 },
  "Destination Name": { size: 200, minSize: 140, maxSize: 360 },
  "Destination City": { size: 160, minSize: 120, maxSize: 260 },
  "Destination State":{ size: 140, minSize: 80,  maxSize: 200 },
  "PO Number":        { size: 140, minSize: 100, maxSize: 260 },
  "Comments":         { size: 200, minSize: 120, maxSize: 400 },
  "Pickup Date":      { size: 160, minSize: 120, maxSize: 260 },
  "Deliver Date":     { size: 160, minSize: 120, maxSize: 260 },
  "Weight":           { size: 100, minSize: 80,  maxSize: 160 },
  "Pallets":          { size: 100, minSize: 80,  maxSize: 160 },
  "Requested Mode":   { size: 140, minSize: 100, maxSize: 220 },
  "Created Date":     { size: 160, minSize: 120, maxSize: 260 },
};

const SORT_KEY_MAP = {
  "Order Number":     "orderNumber",
  "Office Code":      "officeCode",
  "Status":           "status",
  "Shipment Number":  "shipmentNumber",
  "Origin Name":      "origin",
  "Payment Method":   "paymentMethod",
  "Destination Name": "destination",
  "Destination City": "destinationCity",
  "Destination State":"destinationState",
  "PO Number":        "poNumber",
  "Comments":         "comments",
  "Pickup Date":      "pickupDate",
  "Deliver Date":     "deliveryDate",
  "Weight":           "weight",
  "Pallets":          "numberOfPallets",
  "Requested Mode":   "requestedMode",
  "Created Date":     "createdDate",
};

const DEFAULT_STICKY_COLUMN_KEYS = ["Action", "Selection", "Order Number"];

/** Default status quick-filter when no saved view is active (matches ordersSlice). */
const DEFAULT_QUICK_FILTER = "New";

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const OrdersTableIncoming = ({
  searchQuery = "",
  statusFilter,
  searchTrigger,
  setTotalOrders,
  statusOptions = ["New", "Cancelled", "Planned", "Completed"],
  stateKey = "orders-incoming",
}) => {
  const token           = Cookies.get("authToken");
  const officeCode      = Cookies.get("officeCode");
  const manyOfficeCOdes = Cookies.get("officeCodes");

  const dropdownRef       = useRef(null);
  const bulkMenuRef       = useRef(null);
  const checkboxHeaderRef = useRef(null);
  const tableContainerRef = useRef(null);

  const getBulkMenuStyle = () => {
    const container = tableContainerRef.current;
    const header = checkboxHeaderRef.current;
    if (!container || !header) return null;

    const checkbox = header.querySelector('input[type="checkbox"]');
    const anchor = checkbox ?? header;
    const anchorRect = anchor.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return {
      top:
        anchorRect.bottom - containerRect.top + container.scrollTop + 6,
      left:
        anchorRect.left -
        containerRect.left +
        container.scrollLeft +
        anchorRect.width / 2,
      transform: "translateX(-50%)",
    };
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const DISABLED_STATUSES = new Set(["CANCELLED", "CANCELED"]);

  const { tabs }      = useSelector(selectModuleTabs("orders"));
  const listViewState = useSelector((s) => s.orders.listViewState || {});
  const savedView     = listViewState[stateKey] || {};

  const [showBulkMenu,    setShowBulkMenu]    = useState(false);
  const [selectedOrders,  setSelectedOrders]  = useState([]);
  const [isCreatingShipment, setIsCreatingShipment] = useState(false);
  const [openDropdownRow, setOpenDropdownRow] = useState(null);
  const [isLoading,       setIsLoading]       = useState(true);
  const [orders,          setOrders]          = useState([]);
  const [currentPage,     setCurrentPage]     = useState(savedView.currentPage  || 1);
  const [ordersPerPage,   setOrdersPerPage]   = useState(savedView.itemsPerPage || 50);
  const [totalPages,      setTotalPages]      = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orderToDelete,   setOrderToDelete]   = useState(null);
  const [sortBy,          setSortBy]          = useState("Pickup Date");
  const [ascending,       setAscending]       = useState(false);
  const [dropdownPos,     setDropdownPos]     = useState({ top: 0, left: 0 });

  const prevStatusFilterRef = useRef(statusFilter);

  const getExtraConfig = useCallback(
    () => ({ sortBy, ascending, filter: statusFilter, currentPage, pageSize: ordersPerPage }),
    [sortBy, ascending, statusFilter, currentPage, ordersPerPage]
  );

  const handleApplySavedViewConfig = useCallback((config, _viewMeta, options = {}) => {
    if (config.sortBy !== undefined) setSortBy(config.sortBy);
    if (config.ascending !== undefined) setAscending(config.ascending);
    if (config.currentPage) setCurrentPage(config.currentPage);
    if (config.pageSize) setOrdersPerPage(config.pageSize);
    if (config.filter !== undefined) {
      dispatch(setStatusFilter(config.filter));
      dispatch(setListViewState({ stateKey, filter: config.filter }));
    } else if (options.resetExtraConfig) {
      dispatch(setStatusFilter(DEFAULT_QUICK_FILTER));
      dispatch(setListViewState({ stateKey, filter: DEFAULT_QUICK_FILTER }));
    }
  }, [dispatch, stateKey]);

  const tableViews = useTableSavedViews({
    pageKey: stateKey || TABLE_PAGE_KEYS.ORDERS_INCOMING,
    allHeaders: TABLE_HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: DEFAULT_STICKY_COLUMN_KEYS,
    getExtraConfig,
    onApplyConfig: handleApplySavedViewConfig,
  });

  const {
    columnWidths: savedColumnWidths,
    setColumnWidths: onColumnResize,
    dataHeaders: savedDataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    markDirty,
    reconcileDirtyState,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  const prevQuickFilterForDirtyRef = useRef(statusFilter);

  // Reconcile dirty when the user changes the quick filter, not when activeView updates after save.
  useEffect(() => {
    if (!activeView || viewsLoading) return;

    const quickFilterChanged = prevQuickFilterForDirtyRef.current !== statusFilter;
    prevQuickFilterForDirtyRef.current = statusFilter;
    if (!quickFilterChanged) return;

    reconcileDirtyState();
  }, [activeView, statusFilter, viewsLoading, reconcileDirtyState]);

  // ── Persist pagination + quick filter ─────────────────────────────────────
  useEffect(() => {
    dispatch(setListViewState({
      stateKey,
      currentPage,
      itemsPerPage: ordersPerPage,
      filter: statusFilter,
    }));
  }, [dispatch, stateKey, currentPage, ordersPerPage, statusFilter]);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchOrders = async () => {
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/order-details/search?page=${currentPage - 1}&size=${ordersPerPage}`,
        { query: searchQuery.trim(), status: statusFilter },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "Office-Code": manyOfficeCOdes,
          },
        }
      );
      setOrders(data.content);
      setTotalOrders(data.totalElements);
      setTotalPages(data.totalPages);
    } catch (err) {
      setOrders([]);
      setTotalOrders(0);
      setTotalPages(1);
      console.error("Error fetching orders:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const statusChanged = prevStatusFilterRef.current !== statusFilter;
    if (statusChanged && currentPage !== 1) {
      prevStatusFilterRef.current = statusFilter;
      setCurrentPage(1);
      return;
    }
    prevStatusFilterRef.current = statusFilter;
    fetchOrders();
  }, [currentPage, ordersPerPage, searchTrigger, statusFilter, searchQuery]);

  // ── Click-outside handlers ────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenDropdownRow(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (
        bulkMenuRef.current     && !bulkMenuRef.current.contains(e.target) &&
        checkboxHeaderRef.current && !checkboxHeaderRef.current.contains(e.target)
      ) setShowBulkMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Derived data ──────────────────────────────────────────────────────────
  const disabledByOrder = React.useMemo(() => {
    const m = new Map();
    for (const o of orders)
      m.set(makeKey(o.orderNumber, o.officeCode), DISABLED_STATUSES.has((o.status || "").toUpperCase()));
    return m;
  }, [orders]);

  const sortedOrders = React.useMemo(() => {
    if (!sortBy) return orders;
    const key = SORT_KEY_MAP[sortBy];
    if (!key) return orders;
    return [...orders].sort((a, b) => {
      const av = a[key] ?? "";
      const bv = b[key] ?? "";
      if (typeof av === "number" && typeof bv === "number") return ascending ? av - bv : bv - av;
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
      return ascending ? cmp : -cmp;
    });
  }, [orders, sortBy, ascending]);

  const formattedOrders = React.useMemo(() => sortedOrders.map((order) => ({
    // Utility cols — rendered manually in children, value unused
    "Action":    null,
    "Selection": null,
    // Data cols
    "Order Number":      order.orderNumber,
    "Office Code":       order.officeCode,
    "Status": (
      <span className={`text-black text-fluid-sm px-fluid-1.5 py-fluid-1 rounded font-medium ${
        statusBadgeClass[(order?.status || "").toUpperCase()] || statusBadgeClass.DEFAULT
      }`}>
        {(order?.status || "-").toUpperCase()}
      </span>
    ),
    "Shipment Number": order.shipmentNumber
      ? (
        <button
          onClick={() => handleViewShipment(order.shipmentNumber, order.officeCode)}
          className="hover:text-blue-800 dark:hover:text-blue-300 underline cursor-pointer transition-colors"
        >
          {order.shipmentNumber}
        </button>
      ) : "-",
    "Origin Name":      order.origin,
    "Payment Method":   order.paymentMethod,
    "Destination Name": order.destination,
    "Destination City": order.destinationCity  ?? "-",
    "Destination State":order.destinationState ?? "-",
    "PO Number":        order.poNumber,
    "Comments":         order.comments || "-",
    "Pickup Date":      OrderUtil.fmtDateTime24(order.pickupDate),
    "Deliver Date":     OrderUtil.fmtDateTime24(order.deliveryDate),
    "Weight":           order.weight,
    "Pallets":          order.numberOfPallets,
    "Requested Mode":   order.requestedMode,
    "Created Date":     OrderUtil.fmtDateTime24(order.createdDate),
    // Raw values for key generation (prefixed _ so they don't collide with headers)
    _orderNumber: order.orderNumber,
    _officeCode:  order.officeCode,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [sortedOrders]);

  const allEnabledOrders = formattedOrders.filter(
    (r) => !disabledByOrder.get(makeKey(r._orderNumber, r._officeCode))
  );
  const allSelected =
    allEnabledOrders.length > 0 &&
    allEnabledOrders.every((r) => selectedOrders.includes(makeKey(r._orderNumber, r._officeCode)));

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSort = (headerLabel) => {
    if (sortBy === headerLabel) setAscending((p) => !p);
    else { setSortBy(headerLabel); setAscending(true); }
    markDirty();
  };

  const handleSelectAll = () => {
    const enabledKeys = allEnabledOrders.map((r) => makeKey(r._orderNumber, r._officeCode));
    setSelectedOrders(allSelected ? [] : enabledKeys);
  };

  const handleSingleCheckbox = (key) => {
    if (disabledByOrder.get(key)) return;
    setSelectedOrders((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleActionClick = (orderNumber, oc, e) => {
    e.stopPropagation();
    const key  = makeKey(orderNumber, oc);
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenDropdownRow((prev) => (prev === key ? null : key));
  };

  const handleViewOrder = (orderNumber, oc) => {
    const tabId         = `order-${orderNumber}-${oc}`;
    const existingIndex = tabs.findIndex(
      (t) => t.contentType === "orderView" && t.orderNumber === orderNumber && t.orderOfficeCode === oc
    );
    if (existingIndex !== -1) { dispatch(setActiveTab({ index: existingIndex })); return; }
    dispatch(addTab({ id: tabId, name: `Order: ${orderNumber}`, contentType: "orderView", orderNumber, orderOfficeCode: oc, isDynamic: true, createdAt: Date.now() }));
    dispatch(setActiveTab({ index: tabs.length }));
  };

  const handleViewShipment = (shipmentNumber, oc) => {
    dispatch(addShipmentTab({ id: `view-${shipmentNumber}-${Date.now()}`, name: `${shipmentNumber}`, contentType: "shipmentView", shipmentNumber, shipmentOfficeCode: oc, isDynamic: true, createdAt: Date.now() }));
    navigate("/dashboard/shipments");
  };

  const handleEditOrder  = (orderId) => console.log("Editing Order:", orderId);

  const deleteOrder = (orderNumber, oc) => {
    const order = orders.find((o) => o.orderNumber === orderNumber && o.officeCode === oc);
    if (order) { setOrderToDelete(order); setShowDeleteModal(true); }
  };

  const cancelSelectedOrders = async (ordersToCancel = null) => {
    const orderKeys = ordersToCancel || selectedOrders;
    if (!orderKeys.length) {
      notify.error({ title: "No Orders Selected", message: "Please select at least one order to cancel." });
      return;
    }
    const payload = { orders: orderKeys.map((key) => { const [on, oc] = key.split("::"); return { orderNumber: on, officeCode: oc }; }) };
    try {
      const res    = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/orders/cancel-orders`,
        payload,
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      const failed = res.data.filter((r) => r.status === "FAILED");
      if (failed.length)
        notify.warning({ title: "Some Orders Failed to Cancel", message: failed.map((r) => `${r.orderNumber} (${r.message})`).join("\n") });
      else
        notify.success({ title: "Orders Cancelled", message: "All selected orders were cancelled successfully." });
      await fetchOrders();
      setSelectedOrders([]);
      setShowDeleteModal(false);
      setOrderToDelete(null);
    } catch (err) {
      notify.error({ title: "Cancel Failed", message: err?.response?.data?.message || err.message });
    }
  };

  const createShipmentFromSelectedOrders = async () => {
    if (!selectedOrders.length || isCreatingShipment) return;
    const selectedOrderObjs = orders.filter((o) => selectedOrders.includes(makeKey(o.orderNumber, o.officeCode)));
    const normalize         = (v) => (v || "").trim().toUpperCase();
    const nonNew            = selectedOrderObjs.filter((o) => normalize(o.status) !== "NEW");
    if (nonNew.length) {
      notify.error({ title: "Shipment Creation Failed", message: `Orders already planned or cancelled: ${nonNew.map((o) => o.orderNumber).join(", ")}` });
      return;
    }
    const officeCodeForHeader = selectedOrderObjs[0]?.officeCode;
    const payload = { orders: selectedOrders.map((key) => { const [on, oc] = key.split("::"); return { orderNumber: on, officeCode: oc }; }) };
    setIsCreatingShipment(true);
    try {
      // Order status and shipmentNumber are both stamped onto each order in the same
      // backend transaction that creates the shipment — no follow-up client call needed.
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/shipments/createFromOrdersKeys`,
        payload,
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "Office-Code": officeCodeForHeader } }
      );
      notify.success({ title: "Shipment Created", message: `Shipment ${res.data?.shipmentNumber || ""} created from orders ${selectedOrderObjs.map((o) => o.orderNumber).join(", ")}.` });
      setSelectedOrders([]);
      await fetchOrders?.();
    } catch (err) {
      notify.error({ title: "Shipment Creation Failed", message: toErrorMessage(err) });
    } finally {
      setIsCreatingShipment(false);
    }
  };

  // ── Row-action <td> — receives getStickyCellProps so it tracks pinning ────
  const renderActionColumn = (row, disabled, getStickyCellProps) => {
    const id     = row._orderNumber;
    const oc     = row._officeCode;
    const key    = makeKey(id, oc);
    const isOpen = openDropdownRow === key;

    const { className: stickyClass, style: stickyStyle } = getStickyCellProps("Selection");

    const actions = disabled
      ? [{ icon: "Eye",  label: "View",         color: "blue",  action: () => handleViewOrder(id, oc) }]
      : [
          { icon: "Eye",  label: "View",         color: "blue",  action: () => handleViewOrder(id, oc) },
          { icon: "Edit", label: "Edit",         color: "green", action: () => handleEditOrder(id, oc) },
          { icon: "X",    label: "Cancel Order", color: "red",   action: () => deleteOrder(id, oc) },
        ];

    return (
      <>
        <td
          onClick={(e) => e.stopPropagation()}
          className={`border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${stickyClass}`}
          style={{ ...stickyStyle, width: "50px", minWidth: "50px", maxWidth: "50px" }}
        >
          <button
            data-action-trigger
            className={`p-1 w-full transition-none ${disabled ? "opacity-60 hover:opacity-80" : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text"}`}
            onClick={(e) => handleActionClick(id, oc, e)}
          >
            <MoreHorizontal size={18} className="mx-auto" />
          </button>
        </td>

        {isOpen && ReactDOM.createPortal(
          <div ref={dropdownRef} style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}>
            <ActionMenu rowId={key} isOpen={isOpen} onClose={() => setOpenDropdownRow(null)} moduleName="Order" actions={actions} />
          </div>,
          document.body
        )}
      </>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
      {isLoading ? (
        <Loader color="text-blue-600 dark:text-acs-dark-text" size="h-5 w-5" message="Loading Orders..." />
      ) : (
        <>
        <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
          <TableSavedViewsControl
            controlLabel={controlLabel}
            views={views}
            activeView={activeView}
            loading={viewsLoading}
            error={viewsError}
            isDirty={isDirty}
            allDataHeaders={TABLE_HEADERS.slice(2)}
            dataHeaders={savedDataHeaders}
            onSelectView={selectView}
            onSaveNew={saveNewView}
            onUpdateActive={updateActiveView}
            onRename={renameView}
            onDelete={deleteView}
            onSetDefault={setDefaultView}
            onUpdateColumnVisibility={updateColumnVisibility}
            onManageColumns={onManageColumns}
          />
        </div>
        <div
          ref={tableContainerRef}
          className="flex-1 min-h-0 sm:min-h-[300px] overflow-x-auto overflow-y-auto bg-white dark:bg-acs-dark-surface transition-none relative"
        >
          <Table
            {...columnManagerProps}
            data={formattedOrders}
            onSelectAll={handleSelectAll}
            renderCheckboxActions={() => setShowBulkMenu((p) => !p)}
            checkboxHeaderRef={checkboxHeaderRef}
            selectedRows={selectedOrders}
            totalValidRows={allEnabledOrders.length}
            // ── Pinning ──────────────────────────────────────────────────────
            enableColumnPin
            // ── Sizing ───────────────────────────────────────────────────────
            columnSizeConfig={COLUMN_SIZE_CONFIG}
            enableColumnResize
            initialColumnWidths={savedColumnWidths}
            onColumnResize={onColumnResize}
            // ── Reordering ───────────────────────────────────────────────────
            enableColumnReorder
            nonHideableColumnIds={["Order Number"]}
            // ── Sorting ──────────────────────────────────────────────────────
            onSort={handleSort}
            sortableHeaders={TABLE_HEADERS.slice(2)}
            sortBy={sortBy}
            ascending={ascending}
          >
            {/*
              Args from Table:
                row               — formatted data row
                rowIndex          — row index
                columnWidths      — { [colId]: number } live pixel widths
                getStickyCellProps— (colId | colIndex) => { className, style }
                orderedColumnIds  — all visible column IDs in current visual order
                                    (left-pinned → center → right-pinned, respecting drag order)
                                    ALWAYS use this for rendering body cells — never TABLE_HEADERS.slice(2)
            */}
            {(row, rowIndex, columnWidths, getStickyCellProps, visibleColumns) => {
              const key      = makeKey(row._orderNumber, row._officeCode);
              const disabled = disabledByOrder.get(key);

              // Props for the checkbox cell (col "Action")
              const { className: checkboxClass, style: checkboxStyle } =
                getStickyCellProps("Action");

              // Strip the two utility columns; what remains is in the current
              // pin + drag order — exactly matching the header column sequence.
              const orderedDataHeaders = (visibleColumns ?? [])
                .map((col) => col.id)
                .filter((id) => id !== "Action" && id !== "Selection");
              const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : savedDataHeaders;

              return (
                <>
                  {/* ── Col 0 — Checkbox ────────────────────────────────── */}
                  <td
                    className={`border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${checkboxClass}`}
                    style={{ ...checkboxStyle, width: "80px", minWidth: "80px", maxWidth: "80px" }}
                    onDoubleClick={() => handleViewOrder(row._orderNumber, row._officeCode)}
                  >
                    <input
                      type="checkbox"
                      className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox dark:checked:border-acs-checkbox-dark after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                      checked={selectedOrders.includes(key)}
                      disabled={disabled}
                      onChange={() => handleSingleCheckbox(key)}
                    />
                  </td>

                  {/* ── Col 1 — Row-action button (owns its own <td>) ────── */}
                  {renderActionColumn(row, disabled, getStickyCellProps)}

                  {/* ── Cols 2+ — Data cells in current column order ──────── */}
                  {dataHeadersToRender.map((header) => {
                    const { className: colClass, style: colStyle } = getStickyCellProps(header);
                    const colWidth = columnWidths[header] ?? COLUMN_SIZE_CONFIG[header]?.size ?? 200;

                    return (
                      <td
                        key={header}
                        className={`px-2 sm:px-3 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm border-b border-gray-200 dark:border-acs-dark-border transition-none font-roboto ${colClass}`}
                        style={{
                          ...colStyle,
                          width:         `${colWidth}px`,
                          minWidth:      `${colWidth}px`,
                          maxWidth:      `${colWidth}px`,
                          whiteSpace:    "nowrap",
                          overflow:      "hidden",
                          textOverflow:  "ellipsis",
                        }}
                        onDoubleClick={() => handleViewOrder(row._orderNumber, row._officeCode)}
                      >
                        {row[header]}
                      </td>
                    );
                  })}
                </>
              );
            }}
          </Table>

          {/* ── Bulk-action menu ────────────────────────────────────────── */}
          {showBulkMenu &&
            (() => {
              const bulkMenuStyle = getBulkMenuStyle();
              if (!bulkMenuStyle) return null;

              return (
                <div
                  ref={bulkMenuRef}
                  className="absolute z-[1000]"
                  style={bulkMenuStyle}
                >
                  <ActionMenu
                    rowId="bulk"
                    isOpen
                    onClose={() => setShowBulkMenu(false)}
                    moduleName="Order"
                    placement="header"
                    actions={[{
                      icon: "FilePlus",
                      label: "Create Shipment(s)",
                      color: "green",
                      action: async () => { await createShipmentFromSelectedOrders(); setShowBulkMenu(false); },
                    }]}
                  />
                </div>
              );
            })()}
        </div>
        </>
      )}

      <GlobalPagination
        label="Orders per page"
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={ordersPerPage}
        onItemsPerPageChange={setOrdersPerPage}
      />

      {showDeleteModal && orderToDelete && (
        <CancelOrderModal
          onClose={() => { setShowDeleteModal(false); setOrderToDelete(null); }}
          onConfirm={() => cancelSelectedOrders([makeKey(orderToDelete.orderNumber, orderToDelete.officeCode)])}
          orderDetails={orderToDelete}
        />
      )}
    </div>
  );
};

export default OrdersTableIncoming;